/*
 * Define a form page to display a list of HHS Programs, OS Divisions, and Business Units.
 */
import * as yup from 'yup'
import css from './pages.module.css'
import { convertHashToChoiceArray } from '../choices/convertHashToChoiceArray'
import { officeNameLengthMessage, requiredMessage } from '../strings/messages'
import { ProgramSearch } from '../components/ProgramSearch'
import {
  dont_know,
  hhs_programs,
  office_of_the_secretary,
  hhs_business_units
} from 'hotline_complaint_choices'

const dontKnow = Object.keys(dont_know)[0]
const dontKnowLabel = Object.values(dont_know)[0]

export const addDefaultOption = (
  choices,
  defaultLabel = '',
  includeDontKnow = false
) => {
  const newChoices = [{ label: defaultLabel, value: '' }].concat(choices)
  return includeDontKnow
    ? newChoices.concat([{ label: dontKnowLabel, value: dontKnow }])
    : newChoices
}

export const getOptions = (object, defaultLabel, includeDontKnow) =>
  addDefaultOption(
    convertHashToChoiceArray(object),
    defaultLabel,
    includeDontKnow
  )

export const hhsProgramsPage = () => {
  const programChoices = getOptions(hhs_programs).filter(
    choice => choice.value !== 'FRAUD_GOV'
  )
  const offOfSecretaryChoices = getOptions(office_of_the_secretary)
  const businessUnits = Object.keys(hhs_business_units).reduce(
    (acc, key) => ({
      ...acc,
      [key]: getOptions(hhs_business_units[key], '', true)
    }),
    {}
  )

  const additionalDetailField = {
    label: 'Office name',
    labelClassName: `${css.mediumLabel} ${css.officeName}`,
    type: 'text',
    name: 'hhs_program_detail'
  }

  const dontKnowHint =
    'To the best of your ability, please provide as much information as you can about the relevant program or office.'
  const knownHint =
    'To the best of your ability, please provide additional details to fully identify the relevant office. For example: Office of the Inspector General/Office of Investigations/Investigations Branch/Hotline Operations.'

  const knownDetailCondition = {
    value: 'ANY',
    not: dontKnow,
    fields: [
      {
        ...additionalDetailField,
        hint: knownHint
      }
    ]
  }

  const dontKnowDetailCondition = {
    value: dontKnow,
    fields: [
      {
        ...additionalDetailField,
        hint: dontKnowHint
      }
    ]
  }

  return {
    id: 'hhs_program_page',
    title: 'Program and business unit details',
    className: css.hhsPrograms,
    description:
      'Please provide the program and business unit that this allegation is related to.',
    validation: yup.object().shape({
      business_unit: yup
        .string()
        .when(
          ['hhs_program', 'office_of_secretary'],
          (hhs_program, office_of_secretary, schema) => {
            const isOptional = [hhs_program, office_of_secretary].includes(
              dontKnow
            )
            return isOptional ? schema : schema.required(requiredMessage)
          }
        ),
      hhs_program: yup.string().required(requiredMessage),
      hhs_program_detail: yup.string().max(75, officeNameLengthMessage),
      office_of_secretary: yup.string().when('hhs_program', {
        is: 'OS',
        then: yup.string().required(requiredMessage),
        otherwise: yup.string()
      })
    }),
    fields: [
      {
        type: 'content',
        content: ProgramSearch
      },
      {
        name: 'hhs_program',
        choices: programChoices,
        label: 'Program',
        type: 'select',
        className: `${css.choices} ${css.largeField}`,
        labelClassName: css.mediumLabel,
        conditionals: [
          {
            value: 'ANY',
            not: ['OS', dontKnow],
            fields: [
              {
                type: 'select',
                choices: {
                  parentField: 'hhs_program',
                  choices: businessUnits
                },
                name: 'business_unit',
                label: 'Business unit',
                className: `${css.choices} ${css.largeField}`,
                labelClassName: css.mediumLabel,
                conditionals: [knownDetailCondition, dontKnowDetailCondition]
              }
            ]
          },
          {
            value: 'OS',
            fields: [
              {
                type: 'select',
                choices: offOfSecretaryChoices,
                name: 'office_of_secretary',
                label: 'Staff division',
                className: `${css.choices} ${css.largeField}`,
                labelClassName: css.mediumLabel,
                conditionals: [
                  {
                    value: 'ANY',
                    not: dontKnow,
                    fields: [
                      {
                        type: 'select',
                        choices: {
                          parentField: 'office_of_secretary',
                          choices: businessUnits
                        },
                        name: 'business_unit',
                        label: 'Business unit',
                        className: `${css.choices} ${css.largeField}`,
                        labelClassName: css.mediumLabel,
                        conditionals: [
                          knownDetailCondition,
                          dontKnowDetailCondition
                        ]
                      }
                    ]
                  },
                  dontKnowDetailCondition
                ]
              }
            ]
          },
          dontKnowDetailCondition
        ]
      }
    ]
  }
}
